/**
 * Create a typed action definition with an optional description.
 *
 * The generic parameter carries the expected payload type at compile time.
 *
 * @example
 * ```ts
 * type UserPayload = { userId: string; email: string };
 * const created = action<UserPayload>("Emitted when a user is created");
 * ```
 */
export function action(description) {
    return { description };
}
/**
 * Define a typed notification stream contract.
 *
 * The returned contract is used with `notifications.register()`,
 * `notifications.emit()`, and `notifications.subscribe()` to get
 * compile-time type safety on action names and payload shapes.
 *
 * @example
 * ```ts
 * import { defineNotificationStream, action } from "@legal-500/discovery";
 *
 * type SubscriptionPayload = { subscriptionId: string; firmId: number };
 *
 * export const subscriptionNotifications = defineNotificationStream({
 *   service: "fulfilment",
 *   stream: "subscription",
 *   description: "Subscription lifecycle events",
 *   actions: {
 *     created: action<SubscriptionPayload>("Emitted when a subscription is created"),
 *     updated: action<SubscriptionPayload>("Emitted when a subscription is modified"),
 *     cancelled: action<SubscriptionPayload>("Emitted when a subscription is cancelled"),
 *   },
 * });
 * ```
 */
export function defineNotificationStream(config) {
    const actionDeclarations = Object.entries(config.actions).map(([actionName, def]) => ({
        action: actionName,
        description: def.description,
    }));
    return {
        service: config.service,
        stream: config.stream,
        description: config.description,
        actions: config.actions,
        declaration: {
            stream: `${config.service}:${config.stream}`,
            description: config.description,
            actions: actionDeclarations,
        },
    };
}
/**
 * Type-safe helper to build a subscription input for
 * {@link DiscoveryClient.notifications.declareSubscriptions}.
 *
 * @example
 * ```ts
 * import { subscriptionTo } from "@legal-500/discovery";
 * import { subscriptionNotifications } from "@legal-500/discovery/contracts/fulfilment";
 *
 * discovery.notifications.declareSubscriptions([
 *   subscriptionTo(subscriptionNotifications, ["created", "updated"]),
 * ]);
 * ```
 */
export function subscriptionTo(contract, actions) {
    return { contract, actions };
}
//# sourceMappingURL=define.js.map